import * as React from 'react'
import styled from 'styled-components'
import { contentfulLoader } from '../../../../services/images'
import { config, Hidden } from 'react-awesome-styled-grid'
import { GoodSansH1, QueensH3, GoodSansB2 } from '@components/shared/typography'
import { CTAPrimary } from '@components/shared/buttons'
import Image from 'next/legacy/image'
import { colors } from '@components/shared/colors'

export type PropsType = {
  resultHeader: string,
  image: string,
  name: string,
  description: string,
  isSubscribed: boolean,
  existingResult?: string,
  triggerConversionEvent: () => void
}

export type DispatchPropsType = {
  openSubscribeModal: () => void,
  openDriveToDownload: () => void
}

export default function QuizResults (
  props: PropsType & DispatchPropsType
): React.ReactNode {
  React.useEffect((): void => {
    if (!props.existingResult) {
      props.triggerConversionEvent()
    }
  }, [])

  const buttonAction = (): void => {
    if (!props.isSubscribed) {
      props.openSubscribeModal()
    } else {
      props.openDriveToDownload()
    }
  }

  const mobileBtnText = props.isSubscribed
    ? `START LISTENING`
    : `UNLOCK FOR FREE`
  const tabletBtnText = props.isSubscribed
    ? `LISTEN TO ${(props?.name || '').toUpperCase()}`
    : `UNLOCK ${(props?.name || '').toUpperCase()} FOR FREE`

  return (
    <Section>
      <Header>{props.resultHeader || 'Your Perfect Match Is...'}</Header>

      <ResultContainer>
        {props.image && (
          <ImageContainer>
            <Image
              loader={contentfulLoader}
              src={props.image}
              quality={50}
              width={400}
              height={400}
              priority
            />
          </ImageContainer>
        )}

        {props.name && <DisplayName>{props.name}</DisplayName>}

        {props.description && (
          <DescriptionText>{props.description}</DescriptionText>
        )}
      </ResultContainer>

      <OrangeButtonContainer>
        <Hidden xs={false} sm={true} md={true} lg={true} xl={true}>
          <OrangeButton onClick={buttonAction}>{mobileBtnText}</OrangeButton>
        </Hidden>
        <Hidden xs={true} sm={false} md={false} lg={false} xl={false}>
          <OrangeButton onClick={buttonAction}>{tabletBtnText}</OrangeButton>
        </Hidden>
      </OrangeButtonContainer>
    </Section>
  )
}

const OrangeButtonContainer = styled.div<any>`
  display: flex;
  justify-content: center;
  padding: 0 20px 60px;
`
const OrangeButton = styled(CTAPrimary)<any>`
  font-family: 'Good Sans Bold';
  height: unset;
  min-height: 40px;
  width: 100%;
  ${(props: any): any => config(props).media.md`
    padding: 10px 60px;
    width: unset;
  `}
`
const ImageContainer = styled.div<any>`
  aspect-ratio: 1;
  background-color: ${colors.midnightBlueTint};
  border-radius: 15px;
  overflow: hidden;
  width: 50vw;
  max-height: 400px;
  max-width: 400px;
  min-height: 184px;
  min-width: 184px;

  margin: 0 0 18px;
  ${(props: any): any => config(props).media.md`
    margin: 0 0 24px;
  `}

  @media (max-height: 800px) and (orientation: landscape) {
    height: 50vh;
    width: unset;
  }

  img {
    height: 100%;
    width: 100%;
  }
`
const Header = styled.h1<any>`
  ${GoodSansH1};
  text-align: center;
  margin: 16px 0 0;
`
const DisplayName = styled.p<any>`
  ${QueensH3};
  margin: 0 0 12px;
  text-align: center;
`
const DescriptionText = styled.p<any>`
  ${GoodSansB2};
  text-align: center;
  max-width: 700px;
`
const Section = styled.section<any>`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  overflow: scroll;

  /* cover space below the nav */
  padding-top: 60px;
  min-height: calc(100vh - 60px);
  ${(props: any): any => config(props).media.md`
    padding-top: 75px;
    min-height: calc(100vh - 75px );
  `}
`
const ResultContainer = styled.div<any>`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 20px;
`
